import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_OVER_HIT_LOOP_OVERLAP_MS, GAME_WIDTH } from '../config/gameConstants';
import { AudioKeys, SceneKeys, TextureKeys } from '../config/keys';
import { AudioManager } from '../managers/AudioManager';
import { GameOverData } from '../types/gameState';

export class GameOverScene extends Phaser.Scene {
  private score = 0;
  private bestScore = 0;
  private audioManager!: AudioManager;
  private hitLoopTimer?: Phaser.Time.TimerEvent;

  constructor() {
    super(SceneKeys.GameOver);
  }

  init(data: GameOverData): void {
    this.score = data.score ?? 0;
    this.bestScore = data.bestScore ?? 0;
  }

  create(): void {
    this.audioManager = AudioManager.get(this);
    this.startHitLoop();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.stopHitLoop());

    this.add.image(0, 0, TextureKeys.PaperBackground).setOrigin(0);
    this.add.image(GAME_WIDTH / 2, 208, TextureKeys.BirdFrame2).setScale(1.7).setAngle(10);

    this.add
      .text(GAME_WIDTH / 2, 318, 'crash landing', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '42px',
        color: '#28221a',
        stroke: '#f7f0dc',
        strokeThickness: 7,
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 406, `score ${this.score}\nbest ${this.bestScore}`, {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '28px',
        color: '#3f3324',
        lineSpacing: 12,
        align: 'center',
      })
      .setOrigin(0.5);

    const retry = this.add
      .text(GAME_WIDTH / 2, 538, 'TRY AGAIN', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '30px',
        color: '#28221a',
        backgroundColor: '#f8d36d',
        padding: { x: 28, y: 14 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    const menu = this.add
      .text(GAME_WIDTH / 2, 620, 'MENU', {
        fontFamily: '"Trebuchet MS", "Comic Sans MS", sans-serif',
        fontSize: '23px',
        color: '#28221a',
        backgroundColor: '#d8ead0',
        padding: { x: 25, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    retry.on(Phaser.Input.Events.POINTER_DOWN, () => this.restart());
    menu.on(Phaser.Input.Events.POINTER_DOWN, () => this.goMenu());
    this.input.keyboard?.once('keydown-SPACE', () => this.restart());
    this.input.keyboard?.once('keydown-ENTER', () => this.restart());

    this.addDoodle();
  }

  private restart(): void {
    this.stopHitLoop();
    this.audioManager.play(AudioKeys.MenuSelect);
    this.scene.start(SceneKeys.Game);
  }

  private goMenu(): void {
    this.stopHitLoop();
    this.audioManager.play(AudioKeys.MenuSelect);
    this.scene.start(SceneKeys.Menu);
  }

  private startHitLoop(): void {
    const hit = this.audioManager.play(AudioKeys.Hit);
    if (!hit) {
      return;
    }

    const delay = Math.max(hit.duration * 1000 - GAME_OVER_HIT_LOOP_OVERLAP_MS, 16);
    this.hitLoopTimer = this.time.addEvent({
      delay,
      loop: true,
      callback: () => this.audioManager.play(AudioKeys.Hit),
    });
  }

  private stopHitLoop(): void {
    this.hitLoopTimer?.remove(false);
    this.hitLoopTimer = undefined;
    this.audioManager.stop(AudioKeys.Hit);
  }

  private addDoodle(): void {
    const graphics = this.add.graphics();
    graphics.lineStyle(3, 0x28221a, 0.38);
    graphics.beginPath();
    graphics.moveTo(116, GAME_HEIGHT - 142);
    graphics.lineTo(138, GAME_HEIGHT - 164);
    graphics.lineTo(160, GAME_HEIGHT - 142);
    graphics.strokePath();
    graphics.strokeCircle(272, GAME_HEIGHT - 150, 22);
    graphics.lineBetween(258, GAME_HEIGHT - 164, 286, GAME_HEIGHT - 136);
    graphics.lineBetween(286, GAME_HEIGHT - 164, 258, GAME_HEIGHT - 136);
  }
}
